import React, { useState } from 'react';
import { X } from 'lucide-react';
interface ContactFormProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ContactForm({
  isOpen,
  onClose
}: ContactFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessUrl: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const formatBusinessUrl = (url: string) => {
    if (!url.trim()) return '';
    // If URL doesn't start with http:// or https://, add https://
    if (!/^https?:\/\//i.test(url)) {
      return `https://${url}`;
    }
    return url;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    // Format the URL before submission
    const formattedData = {
      ...formData,
      businessUrl: formatBusinessUrl(formData.businessUrl)
    };
    try {
      const response = await fetch('https://formspree.io/f/movlpolv', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(formattedData)
      });
      const data = await response.json();
      if (response.ok) {
        setIsSubmitting(false);
        setIsSubmitted(true);
        // Reset form and close after delay
        setTimeout(() => {
          onClose();
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            businessUrl: ''
          });
        }, 3000);
      } else {
        throw new Error(data.error || 'Form submission failed');
      }
    } catch (error) {
      setIsSubmitting(false);
      setSubmitError('There was an error submitting your form. Please try again later.');
      console.error('Form submission error:', error);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-328">
      <div className="bg-white rounded-lg shadow-elegant max-w-md w-full relative overflow-hidden" data-id="element-329">
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-ironsmith-dark transition-colors" aria-label="Close form" data-id="element-330">
          <X size={20} data-id="element-331" />
        </button>
        <div className="p-6 sm:p-8" data-id="element-332">
          {isSubmitted ? <div className="text-center py-8" data-id="element-333">
              <div className="text-ironsmith-gold text-4xl mb-4" data-id="element-334">✓</div>
              <h3 className="text-xl font-serif font-medium text-ironsmith-dark mb-2" data-id="element-335">
                Thank You!
              </h3>
              <p className="text-gray-700" data-id="element-336">
                We've received your information and will be in touch soon.
              </p>
            </div> : <>
              <div className="text-center mb-6" data-id="element-337">
                <h3 className="text-xl font-serif font-medium text-ironsmith-dark mb-2" data-id="element-338">
                  Let's Connect
                </h3>
                <p className="text-sm text-gray-700" data-id="element-339">
                  Share your details and we'll be in touch to discuss how
                  Ironsmith Capital can help your business.
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-340">
                <div data-id="element-341">
                  <label htmlFor="name" className="block text-sm font-medium text-ironsmith-dark mb-1" data-id="element-342">
                    Name <span className="text-red-500" data-id="element-343">*</span>
                  </label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-ironsmith-gold`} placeholder="Your full name" data-id="element-344" />
                  {errors.name && <p className="mt-1 text-xs text-red-500" data-id="element-345">{errors.name}</p>}
                </div>
                <div data-id="element-346">
                  <label htmlFor="email" className="block text-sm font-medium text-ironsmith-dark mb-1" data-id="element-347">
                    Email <span className="text-red-500" data-id="element-348">*</span>
                  </label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-ironsmith-gold`} placeholder="your@email.com" data-id="element-349" />
                  {errors.email && <p className="mt-1 text-xs text-red-500" data-id="element-350">{errors.email}</p>}
                </div>
                <div data-id="element-351">
                  <label htmlFor="phone" className="block text-sm font-medium text-ironsmith-dark mb-1" data-id="element-352">
                    Phone <span className="text-red-500" data-id="element-353">*</span>
                  </label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={`w-full px-3 py-2 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-ironsmith-gold`} placeholder="(555) 123-4567" data-id="element-354" />
                  {errors.phone && <p className="mt-1 text-xs text-red-500" data-id="element-355">{errors.phone}</p>}
                </div>
                <div data-id="element-356">
                  <label htmlFor="businessUrl" className="block text-sm font-medium text-ironsmith-dark mb-1" data-id="element-357">
                    Business Website
                  </label>
                  <input type="text" id="businessUrl" name="businessUrl" value={formData.businessUrl} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-ironsmith-gold" placeholder="yourbusiness.com" data-id="element-358" />
                </div>
                {submitError && <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-md text-sm" data-id="element-359">
                    {submitError}
                  </div>}
                <div className="pt-2" data-id="element-360">
                  <button type="submit" disabled={isSubmitting} className="w-full bg-ironsmith-gold text-ironsmith-dark py-2.5 font-medium rounded-md hover:bg-opacity-90 transition-all duration-300 flex items-center justify-center" data-id="element-361">
                    {isSubmitting ? <span className="inline-flex items-center" data-id="element-362">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-ironsmith-dark" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-363">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-364"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-365"></path>
                        </svg>
                        Submitting...
                      </span> : 'Submit'}
                  </button>
                </div>
              </form>
            </>}
        </div>
      </div>
    </div>;
}